import React from 'react';
import { toast } from 'react-toastify';

/**
 * What rides a mowing schedule, and how often — the emerald box on the job
 * page and on Lawn care → Add-ons. One place for the words and the rules:
 *
 *  - changing how often counts from `from` (the visit being looked at, or
 *    today on the Lawn care page); visits already done or billed never move;
 *  - a rule sold a number of times keeps what is used up: the ones already
 *    done come off the number, so counting again never starts the three over;
 *  - a rule on the list's price goes back with no price (`price_is_list`), or
 *    today's list price would become the rule's own and re-price a finished
 *    visit (audit, Oct 1 2026).
 *
 * `rides` is what GET /mow-addons/schedule/:id returns; `onChange` gets the
 * whole new list for PUT /mow-addons/schedule/:id.
 */
export type StandingRule = {
  key: string; name: string; cadence: string; price?: number | null; price_is_list?: boolean;
  minutes?: number; from?: string; times?: number; words?: string; done_on?: string[];
};

/** The body PUT /mow-addons/schedule/:id takes, from what GET gave back. */
export const ruleBody = (list: StandingRule[]) =>
  list.map(a => ({ key: a.key, cadence: a.cadence, price: a.price_is_list ? undefined : a.price, minutes: a.minutes, from: a.from, times: a.times }));

export default function StandingAddonRules({ rides, cadences, from, busy, readOnly, onChange, title = 'Rides this mowing schedule', note = 'Changing how often counts from this visit. Visits already done or billed are never touched.' }: {
  rides: StandingRule[];
  cadences: { key: string; label: string }[];
  from: string;
  busy?: boolean;
  readOnly?: boolean;
  onChange: (list: StandingRule[]) => void;
  title?: string;
  note?: string;
}) {
  if (!rides.length) return null;
  return (
    <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-800">{title}</p>
      <ul className="mt-1 space-y-1.5">
        {rides.map(a => (
          <li key={a.key} className="flex flex-wrap items-center gap-2 text-sm text-gray-900">
            <span className="font-medium">{a.name}</span>
            <span className="text-gray-500">${Number(a.price || 0).toFixed(2)}</span>
            {a.words && <span className="w-full sm:w-auto text-xs text-emerald-900/80">{a.words}</span>}
            <select value={a.cadence} disabled={readOnly || busy} className="input py-1 text-xs w-40"
              onChange={e => {
                // Sold a number of times: the ones already done come off the number, so counting again from
                // this visit does not start the three over.
                const left = a.times ? Number(a.times) - (a.done_on || []).filter((d: string) => d < from).length : undefined;
                if (left !== undefined && left < 1) { toast.error(`All ${a.times} are already done — nothing left to change. Add it again to sell more.`); return; }
                onChange(rides.map(x => (x.key === a.key ? { ...x, cadence: e.target.value, from, times: left } : x)));
              }}>
              {cadences.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <button type="button" onClick={() => { if (window.confirm(`Stop ${a.name} on this schedule? It comes off the visits not done yet.`)) onChange(rides.filter(x => x.key !== a.key)); }}
              disabled={readOnly || busy} className="text-xs text-gray-500 hover:text-red-600">stop</button>
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-emerald-900/70 mt-1">{note}</p>
    </div>
  );
}
